import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import {
  X, ChevronLeft, ChevronRight, Edit3, Check,
  Trash2, Share2
} from 'lucide-react';

export const PhotoLightbox = ({
  lightbox,
  allPhotosFlat,
  editingCaption,
  captionText,
  setCaptionText,
  setEditingCaption,
  onSaveCaption,
  onClose,
  onNav,
  onDelete,
  onShare,
  getPhotoUrl,
}) => {
  if (!lightbox) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="photo-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-testid="close-lightbox">
        <X className="w-6 h-6" />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onDelete(lightbox.photo.id); onClose(); }}
        className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center"
        data-testid="lightbox-delete"
      >
        <Trash2 className="w-5 h-5" />
      </button>

      <button
        onClick={async (e) => { e.stopPropagation(); onShare(lightbox.photo); }}
        className="absolute top-4 left-16 z-10 w-10 h-10 rounded-full bg-ocean/80 hover:bg-ocean text-white flex items-center justify-center"
        data-testid="lightbox-share"
      >
        <Share2 className="w-5 h-5" />
      </button>

      {allPhotosFlat.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onNav(-1); }} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-testid="lightbox-prev">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNav(1); }} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-testid="lightbox-next">
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <div className="max-w-3xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-full flex items-center justify-center" style={{ maxHeight: '70vh' }}>
          <img
            src={getPhotoUrl(lightbox.photo)}
            alt={lightbox.photo.caption || ''}
            className="max-w-full max-h-[70vh] object-contain rounded-lg"
            style={{ maxWidth: '100%', maxHeight: '70vh' }}
          />
        </div>
        <div className="mt-4 w-full max-w-md text-center">
          {editingCaption === lightbox.photo.id ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={captionText}
                onChange={e => setCaptionText(e.target.value)}
                placeholder="Add a caption..."
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder-white/40"
                autoFocus
                data-testid="caption-input"
              />
              <button onClick={() => onSaveCaption(lightbox.photo.id)} className="w-8 h-8 rounded-full bg-gold text-ocean flex items-center justify-center" data-testid="save-caption-btn">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={() => setEditingCaption(null)} className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <p className="text-white/80 text-sm">{lightbox.photo.caption || 'No caption'}</p>
              <button
                onClick={() => { setEditingCaption(lightbox.photo.id); setCaptionText(lightbox.photo.caption || ''); }}
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 text-white/60 flex items-center justify-center"
                data-testid="edit-caption-btn"
              >
                <Edit3 className="w-3 h-3" />
              </button>
            </div>
          )}
          <p className="text-white/40 text-xs mt-1">
            {lightbox.index + 1} / {allPhotosFlat.length} — Day {lightbox.photo.day_number}
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const DeleteConfirmDialog = ({ show, onCancel, onConfirm }) => {
  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" onClick={onCancel} data-testid="delete-confirm-dialog">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        <h3 className="font-serif text-lg text-ocean font-bold mb-2">Delete Photo</h3>
        <p className="text-charcoal/60 text-sm mb-5">Are you sure you want to delete this photo? This action cannot be undone.</p>
        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" size="sm" onClick={onCancel}
            className="text-charcoal/50 h-9 px-4" data-testid="cancel-delete-btn">
            Cancel
          </Button>
          <Button size="sm" onClick={onConfirm}
            className="bg-red-500 hover:bg-red-600 text-white h-9 px-4 rounded-lg" data-testid="confirm-delete-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
